import { useMemo } from 'react';
import { useFieldBindingsQuery } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useFieldBindingsQuery';
import { useSignatureSlotsQuery } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useSignatureSlotsQuery';
import { useDocxFlowDocumentQuery } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useDocxFlowDocument';
import type {
	BindingStatus,
	BindingStatusParams,
	BindingStatusResult,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/components/documentBindingCard.types';

/**
 * What the binding card reports: how many data points are bound and how many signature boxes are
 * placed on this master document. The same reads the editor makes when it opens, so expanding a
 * row warms its cache rather than costing an extra round trip.
 */
export const useDocumentBindingStatus = ({
	templateId,
	isDocx,
	showFieldsTab,
	canPlaceSignaturesManually,
	postDatingEnabled,
	enabled,
}: BindingStatusParams): BindingStatusResult => {
	// A DOCX template holds its bindings in the flow document, a PDF one in coordinate rows, and
	// the two never coexist — so exactly one of these is ever asked for.
	const wantsFields = enabled && showFieldsTab;
	const wantsSignatures = enabled && !isDocx && (canPlaceSignaturesManually || postDatingEnabled);

	const pdfFields = useFieldBindingsQuery(wantsFields && !isDocx ? templateId : null);
	const docxDocument = useDocxFlowDocumentQuery(wantsFields && isDocx ? templateId : null);
	const signatures = useSignatureSlotsQuery(wantsSignatures ? templateId : null);

	const pdfRows = pdfFields.data;
	const docxFields = docxDocument.data?.fields;
	const signatureData = signatures.data;

	const status = useMemo<BindingStatus>(() => {
		const next: BindingStatus = {};

		if (wantsFields && !isDocx && pdfRows) {
			// An empty template is not a binding: generation drops it, so counting it would
			// report a bound field over a blank. Same rule the editor's own progress uses.
			next.fields = {
				done: pdfRows.filter(
					(row) => row.bindingTemplate != null && row.bindingTemplate !== ''
				).length,
				total: pdfRows.length,
			};
		}

		if (wantsFields && isDocx && docxFields) {
			next.fields = {
				done: docxFields.filter((field) => field.bindingTemplate != null).length,
				total: docxFields.length,
			};
		}

		if (wantsSignatures && signatureData) {
			// Slots only need placing by hand when manual placement is on; post-dating adds one
			// date box regardless of how many slots there are.
			const slotsTotal = canPlaceSignaturesManually ? signatureData.slots.length : 0;
			const slotsDone = canPlaceSignaturesManually
				? signatureData.slots.filter((slot) => slot.placement).length
				: 0;
			const dateTotal = postDatingEnabled ? 1 : 0;
			const dateDone = postDatingEnabled && signatureData.datePlacements.length > 0 ? 1 : 0;

			next.signatures = { done: slotsDone + dateDone, total: slotsTotal + dateTotal };
		}

		return next;
	}, [
		wantsFields,
		wantsSignatures,
		isDocx,
		canPlaceSignaturesManually,
		postDatingEnabled,
		pdfRows,
		docxFields,
		signatureData,
	]);

	return {
		status,
		// A disabled query reports `isLoading: false`, so this is true only for a read actually
		// in flight — the card keeps its counts to itself until they are real.
		isLoading: pdfFields.isLoading || docxDocument.isLoading || signatures.isLoading,
	};
};
